import { Block, Done, Refresh, Search } from "@mui/icons-material";
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import React, { useContext, useEffect } from "react";
import { IconBox, ManageJustificationsTables, ManageRequestsTables } from "../components";
import { ManageJustificationsContext, ManageRequestsContext } from "../contexts";
import { useDictionary, useForm } from "../hooks";
import { isDateGreater } from "../hooks/useForm";
import { usePagination } from "../hooks";
import moment from "moment";
import { ROWS_PER_PAGE } from "../hooks/usePagination";

const INITIAL_STATE = {
  firstname: "ALL",
  lastname: "ALL",
  status: "JUSTIFIED",
  statusGroups: "PENDING",
  datefrom: "ALL",
  dateto: "ALL",
};

const ManageJustifications = () => {
  const {
    state: { searchJustifications, searchedJustifications },
    setManageJustifications,approveManageJustification
  } = useContext(ManageJustificationsContext);

  const dictionary = useDictionary();
  const validations = [
    ({ datefrom, dateto }) =>
      isDateGreater(datefrom, dateto) || {
        datefrom: dictionary["reports"]["errors"]["startGreaterEnd"],
      },
  ];

  const {
    values: searchManageJustifications,
    changeHandler,
    errors,
    touched,
    isValid,
    reset,
  } = useForm(searchJustifications, validations);

  useEffect(() => {
    setManageJustifications(INITIAL_STATE);
  }, []);


  return (
    <Stack spacing={2}>
      <Card>
        <IconBox Icon={Search} />
        <CardHeader
          title={dictionary["routes"]["manageJustifications"]}
          sx={{ pl: (theme) => theme.spacing(12) }}
        />
        <CardContent component="form">
          <Grid container spacing={2} mb={1}>
            <Grid item xs={12} md={6} lg={6}>
              <TextField
                size="small"
                fullWidth
                id="text"
                autoComplete="text"
                type="text"
                label={dictionary["managejustification"]["name"]}
                variant="outlined"
                error={Boolean(touched.firstname && errors.firstname)}
                helperText={touched.firstname && errors.firstname}
                value={searchManageJustifications.firstname}
                onChange={(e) => changeHandler("firstname", e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6} lg={6}>
              <TextField
                size="small"
                fullWidth
                id="text"
                autoComplete="text"
                type="text"
                label={dictionary["managejustification"]["lastname"]}
                variant="outlined"
                error={Boolean(touched.lastname && errors.lastname)}
                helperText={touched.lastname && errors.lastname}
                value={searchManageJustifications.lastname}
                onChange={(e) => changeHandler("lastname", e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6} lg={6}>
              <DatePicker
                label={dictionary["reports"]["dateFrom"]}
                inputFormat="DD/MM/yyyy"
                value={searchManageJustifications.datefrom || ""}
                onChange={(newValue) =>
                  changeHandler("datefrom", newValue?.format("yyyy-MM-DD"))
                }
                renderInput={(params) => (
                  <TextField
                    size="small"
                    fullWidth
                    {...params}
                    sx={{ textTransform: "capitalize" }}
                    error={Boolean(touched.datefrom && errors.datefrom)}
                    helperText={touched.datefrom && errors.datefrom}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6} lg={6}>
              <DatePicker
                label={dictionary["reports"]["dateTo"]}
                inputFormat="DD/MM/yyyy"
                value={searchManageJustifications.dateto || ""}
                onChange={(newValue) =>
                  changeHandler("dateto", newValue?.format("yyyy-MM-DD"))
                }
                renderInput={(params) => (
                  <TextField
                    size="small"
                    fullWidth
                    {...params}
                    sx={{ textTransform: "capitalize" }}
                    error={Boolean(touched.dateto && errors.dateto)}
                    helperText={touched.dateto && errors.dateto}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6} lg={6}>
              <FormControl fullWidth size="small">
                <InputLabel id="status-select">{dictionary["managejustification"]["statusrequests"]}</InputLabel>
                <Select
                  id="status-select"
                  value={searchManageJustifications.status || ""}
                  label={dictionary["managejustification"]["statusrequests"]}
                  onChange={(e) => changeHandler("status", e.target.value)}
                >
                  <MenuItem value={"ALL"}>
                    {dictionary["requestStatus"]["ALL"]}
                  </MenuItem>
                  <MenuItem value={"JUSTIFIED"}>
                    {dictionary["anomalies"]["states"]["JUSTIFIED"]}
                  </MenuItem>
                  <MenuItem value={"APPROVED"}>
                    {dictionary["requestStatus"]["APPROVED"]}
                  </MenuItem>
                  <MenuItem value={"NOT_APPROVED"}>
                    {dictionary["requestStatus"]["NOT_APPROVED"]}
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6} lg={6}>
              <FormControl fullWidth size="small">
                <InputLabel id="status-select">
                  {dictionary["managejustification"]["statusreponsegroups"]}
                </InputLabel>
                <Select
                  id="status-select"
                  value={searchManageJustifications.statusGroups || ""}
                  label={dictionary["managejustification"]["statusreponsegroups"]}
                  onChange={(e) =>
                    changeHandler("statusGroups", e.target.value)
                  }
                >
                  <MenuItem value={"ALL"}>
                    {dictionary["requestStatus"]["ALL"]}
                  </MenuItem>
                  <MenuItem value={"PENDING"}>
                    {dictionary["requestStatus"]["PENDING"]}
                  </MenuItem>
                  <MenuItem value={"APPROVED"}>
                    {dictionary["requestStatus"]["APPROVED"]}
                  </MenuItem>
                  <MenuItem value={"NOT_APPROVED"}>
                    {dictionary["requestStatus"]["NOT_APPROVED"]}
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={12} lg={12}>
              <Stack direction="row" justifyContent={"flex-end"} spacing={2}>
                <Button
                  variant="contained"
                  color="success"
                  size="small"
                  sx={{
                    textTransform: "capitalize",
                  }}
                  //disabled={!isValid}
                  startIcon={<Search />}
                  type="submit"
                  onClick={(e) => {
                    e.preventDefault();
                    setManageJustifications(searchManageJustifications);
                  }}
                >
                  {dictionary["search"]}
                </Button>
                <Button
                  variant="contained"
                  color="warning"
                  size="small"
                  sx={{
                    textTransform: "capitalize",
                  }}
                  startIcon={<Refresh />}
                  onClick={reset}
                >
                  {dictionary["reset"]}
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
      <Card>
        <CardContent>
          <ManageJustificationsTables
            mini={false}
            searchForm={searchJustifications}
            searchedJustifications={searchedJustifications}
            approveManageJustification={approveManageJustification}
          />
        </CardContent>
      </Card>
    </Stack>
  );
};

export default ManageJustifications;
